/**
 * Cobro mixto (v12.4.2): un remito cobrado con más de un medio de pago.
 * Espejo del server (server/utils/cobroMixto.js) para la vista previa del
 * editor; el server vuelve a calcular y valida al guardar.
 *
 * Reglas de Rodrigo (22/09/2026):
 *  - El recargo de tarjeta va SOLO sobre la parte que se paga con tarjeta.
 *  - La parte de cuenta corriente carga en la cta cte del cliente solo ese saldo.
 */

export const METODO_MIXTO = 'mixto';
export const LABEL_MIXTO = 'Pago mixto';
export const OPCION_MIXTO = { value: METODO_MIXTO, label: 'Pago mixto (más de un medio)', surcharge: 0 };

/** Renglón tal como lo edita el usuario. `base` = parte del total sin recargo. */
export interface LineaPago {
  key: string;
  method: string;
  base: number;
  accountId?: string;
  paymentInterval?: string;
  chequeNumero?: string;
  chequeBanco?: string;
  chequeVencimiento?: string;
}

export interface LineaCalculada extends LineaPago {
  surchargePct: number;
  surcharge: number;
  amount: number;
}

/** Renglón guardado (remito_payments) tal como lo devuelve el server. */
export interface RemitoPago {
  id?: string;
  method: string;
  base: number;
  surchargePct: number;
  surcharge: number;
  amount: number;
  accountId?: string | null;
  paymentInterval?: number | null;
  checkId?: string | null;
}

export interface Reparto {
  ok: boolean;
  error?: string;
  lineas: LineaCalculada[];
  sumaBase: number;
  faltante: number;
  total: number;
  recargoTotal: number;
}

const TOLERANCIA = 0.5;

let seq = 0;
const nuevaKey = () => `lp-${Date.now().toString(36)}-${(seq++).toString(36)}`;

const num = (v: any): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const red2 = (n: number) => Math.round(num(n) * 100) / 100;

export const pesos = (n: number) => `$${Math.round(num(n)).toLocaleString('es-AR')}`;

export function lineaVacia(method = ''): LineaPago {
  return { key: nuevaKey(), method, base: 0 };
}

/** Dos renglones vacíos: nada preelegido. */
export function lineasIniciales(): LineaPago[] {
  return [lineaVacia(), lineaVacia()];
}

/** Renglones del editor a partir de lo guardado (para editar un remito mixto). */
export function lineasDesdePagos(payments: RemitoPago[] | undefined | null): LineaPago[] {
  const arr = Array.isArray(payments) ? payments : [];
  if (!arr.length) return lineasIniciales();
  return arr.map((p) => ({
    key: nuevaKey(),
    method: p.method,
    base: num(p.base),
    accountId: p.accountId || undefined,
    paymentInterval: p.paymentInterval != null ? String(p.paymentInterval) : undefined
  }));
}

/** Mismo criterio que applySurcharge del server: negativos no se aplican. */
export function conRecargo(base: number, pct: number): { amount: number; surcharge: number } {
  const b = num(base);
  const p = num(pct);
  if (p <= 0) return { amount: b, surcharge: 0 };
  const amount = Math.round(b * (1 + p / 100));
  return { amount, surcharge: amount - b };
}

/**
 * Vista previa del reparto. `recargos` = { codigoMetodo: % } (de payment-options).
 * ok=false trae el motivo en `error` (misma redacción que el server).
 */
export function repartirLineas(base: number, lineas: LineaPago[], recargos: Record<string, number> = {}): Reparto {
  const baseRemito = red2(base);
  const calculadas: LineaCalculada[] = (lineas || []).map((l) => {
    const pct = num(recargos[l.method]);
    const { amount, surcharge } = conRecargo(red2(l.base), pct);
    return { ...l, base: red2(l.base), surchargePct: pct > 0 ? pct : 0, surcharge, amount };
  });
  const sumaBase = red2(calculadas.reduce((s, l) => s + l.base, 0));
  const faltante = red2(baseRemito - sumaBase);
  const total = red2(calculadas.reduce((s, l) => s + l.amount, 0));
  const recargoTotal = red2(calculadas.reduce((s, l) => s + l.surcharge, 0));
  const usadas = calculadas.filter((l) => l.method || l.base > 0);

  let error: string | undefined;
  if (usadas.length < 2) error = 'Un pago mixto necesita al menos dos medios de pago.';
  else if (usadas.some((l) => !l.method)) error = 'Hay un renglón sin medio de pago.';
  else if (usadas.some((l) => !(l.base > 0))) error = 'Hay un renglón con importe en cero.';
  else if (Math.abs(faltante) > TOLERANCIA) {
    error = faltante > 0
      ? `Los importes suman ${pesos(sumaBase)} y el remito es de ${pesos(baseRemito)}: faltan ${pesos(faltante)}.`
      : `Los importes suman ${pesos(sumaBase)} y el remito es de ${pesos(baseRemito)}: sobran ${pesos(-faltante)}.`;
  }
  return { ok: !error, error, lineas: calculadas, sumaBase, faltante, total, recargoTotal };
}

/** Lo que viaja al server en `payments`. */
export function payloadPagos(lineas: LineaPago[]) {
  return (lineas || [])
    .filter((l) => l.method || l.base > 0)
    .map((l) => ({
      method: l.method,
      base: red2(l.base),
      accountId: ['transferencia', 'debito', 'qr'].includes(l.method) ? (l.accountId || null) : null,
      paymentInterval: l.method === 'cuenta_corriente' && l.paymentInterval ? parseInt(l.paymentInterval, 10) : null,
      ...(l.method === 'cheque' && (l.chequeNumero || l.chequeBanco || l.chequeVencimiento)
        ? { chequeData: { checkNumber: l.chequeNumero || '', bank: l.chequeBanco || '', dueDate: l.chequeVencimiento || '' } }
        : {})
    }));
}

/** Completa el renglón `index` con lo que falta para llegar a la base. */
export function completarResto(lineas: LineaPago[], base: number, index: number): LineaPago[] {
  const otros = lineas.reduce((s, l, i) => (i === index ? s : s + num(l.base)), 0);
  const resto = Math.max(0, red2(num(base) - otros));
  return lineas.map((l, i) => (i === index ? { ...l, base: resto } : l));
}

export function esMixto(remito: any): boolean {
  return String(remito?.paymentMethod || '') === METODO_MIXTO;
}

/**
 * Cobros de un remito, uno por medio: un mixto se abre en sus renglones (parte
 * sin recargo, para sumar igual que un remito simple); un remito común es un
 * solo cobro por `montoSimple`.
 */
export function cobrosDeRemito(remito: any, montoSimple?: number): Array<{ method: string; amount: number }> {
  if (esMixto(remito) && Array.isArray(remito.payments) && remito.payments.length) {
    return remito.payments.map((p: RemitoPago) => ({ method: String(p.method || ''), amount: num(p.base) }));
  }
  const monto = montoSimple != null ? num(montoSimple) : (num(remito?.totalContado) || num(remito?.estimatedValue));
  return [{ method: String(remito?.paymentMethod || ''), amount: monto }];
}

/** "Efectivo $60.000 + Tarjeta 3 cuotas $46.000" */
export function resumenPagos(payments: RemitoPago[] | undefined | null, labelDe: (code: string) => string = (c) => c): string {
  return (Array.isArray(payments) ? payments : [])
    .filter((p) => p && p.method)
    .map((p) => `${labelDe(p.method) || p.method} ${pesos(p.amount)}`)
    .join(' + ');
}
